import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle, Filter, Play, FileBadge, Truck, UserCog, RefreshCcw, X as XIcon, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface AlertRow {
  id: string;
  docType: string;
  entityType: 'vehicle' | 'driver';
  entityId: string;
  entityName: string | null;
  entityCode: string | null;
  expiresOn: string;
  daysRemaining: number;
  alertLevel: 'info' | 'warn' | 'urgent' | 'critical' | 'expired';
  alertForDate: string;
  acknowledgedAt: string | null;
  acknowledgedByName: string | null;
  acknowledgmentNotes: string | null;
}

interface Summary {
  byLevel: Record<string, number>;
  expiringIn7Days: number;
  expiringIn30Days: number;
  expired: number;
}

const LEVEL_META: Record<string, { label: string; tone: string; cardTone: string }> = {
  expired:  { label: 'Expired',  tone: 'bg-rose-700/20 text-rose-700 dark:text-rose-300 border-rose-700/40',     cardTone: 'border-rose-700/30' },
  critical: { label: 'Critical', tone: 'bg-rose-500/20 text-rose-700 dark:text-rose-400 border-rose-500/40',     cardTone: 'border-rose-500/30' },
  urgent:   { label: 'Urgent',   tone: 'bg-orange-500/20 text-orange-700 dark:text-orange-400 border-orange-500/40', cardTone: 'border-orange-500/30' },
  warn:     { label: 'Warning',  tone: 'bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/40', cardTone: 'border-amber-500/30' },
  info:     { label: 'Info',     tone: 'bg-sky-500/20 text-sky-700 dark:text-sky-400 border-sky-500/40',         cardTone: 'border-sky-500/30' },
};

const DOC_LABEL: Record<string, string> = {
  vehicle_istimara:          'Istimara',
  vehicle_insurance:         'Insurance',
  vehicle_authorization:     'Naqliat / Authorization',
  driver_iqama:              'Iqama',
  driver_license:            'License',
  driver_health_certificate: 'Health certificate',
  driver_passport:           'Passport',
};

export default function ExpiryDashboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [entityFilter, setEntityFilter] = useState<string>('');
  const [showAck, setShowAck] = useState(false);
  const [renewOpen, setRenewOpen] = useState<AlertRow | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (levelFilter)  params.set('level', levelFilter);
      if (entityFilter) params.set('entityType', entityFilter);
      if (showAck) params.set('showAcknowledged', 'true');
      const [a, s] = await Promise.all([
        apiClient.get<AlertRow[]>('/api/v1/document-expiry/alerts?' + params.toString()),
        apiClient.get<Summary>('/api/v1/document-expiry/dashboard'),
      ]);
      setAlerts(a.data);
      setSummary(s.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [levelFilter, entityFilter, showAck]);

  useEffect(() => { refresh(); }, [refresh]);

  async function runScan() {
    setScanning(true);
    try {
      await apiClient.post('/api/v1/document-expiry/scan');
      await refresh();
    } catch (err) {
      window.alert('Scan failed: ' + extractErrorMessage(err));
    } finally {
      setScanning(false);
    }
  }

  async function ack(a: AlertRow) {
    try {
      await apiClient.post('/api/v1/document-expiry/alerts/' + a.id + '/acknowledge', {});
      await refresh();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button>
          </Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><FileBadge className="h-4 w-4" /> Document expiry</div>
          {isAdmin ? (
            <Button variant="ghost" size="sm" onClick={runScan} disabled={scanning} className="gap-2 h-8">
              {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              Run scan
            </Button>
          ) : <div className="w-24" />}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {/* Summary stat cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <StatCard label="Expired"  value={summary.byLevel.expired ?? 0}  tone={LEVEL_META.expired.cardTone} />
            <StatCard label="Critical" value={summary.byLevel.critical ?? 0} tone={LEVEL_META.critical.cardTone} />
            <StatCard label="Urgent"   value={summary.byLevel.urgent ?? 0}   tone={LEVEL_META.urgent.cardTone} />
            <StatCard label="Warning"  value={summary.byLevel.warn ?? 0}     tone={LEVEL_META.warn.cardTone} />
            <StatCard label="Info"     value={summary.byLevel.info ?? 0}     tone={LEVEL_META.info.cardTone} />
          </div>
        )}

        {/* Filters */}
        <div className="rounded-lg border border-border p-3 flex flex-wrap items-end gap-3">
          <Filter className="h-4 w-4 text-muted-foreground mt-2" />
          <div className="space-y-1">
            <Label htmlFor="level">Level</Label>
            <select id="level" value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}
              className="flex h-11 w-40 rounded-md border border-input bg-background px-3 text-base">
              <option value="">All</option>
              <option value="expired">Expired</option>
              <option value="critical">Critical</option>
              <option value="urgent">Urgent</option>
              <option value="warn">Warning</option>
              <option value="info">Info</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="entity">Entity</Label>
            <select id="entity" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}
              className="flex h-11 w-32 rounded-md border border-input bg-background px-3 text-base">
              <option value="">Both</option>
              <option value="vehicle">Vehicles</option>
              <option value="driver">Drivers</option>
            </select>
          </div>
          <label className="text-sm inline-flex items-center gap-2 mt-7">
            <input type="checkbox" checked={showAck} onChange={(e) => setShowAck(e.target.checked)} />
            Show acknowledged
          </label>
          <Button variant="ghost" size="sm" className="ms-auto gap-2 h-8" onClick={refresh}>
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> {error}
          </div>
        ) : alerts.length === 0 ? (
          <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
            No alerts. Run scan to check current expiries.
          </div>
        ) : (
          <div className="space-y-2">
            {alerts.map((a) => {
              const meta = LEVEL_META[a.alertLevel];
              const EntityIcon = a.entityType === 'vehicle' ? Truck : UserCog;
              return (
                <div key={a.id} className={'rounded-lg border bg-card p-3 flex items-center gap-3 ' + meta.cardTone}>
                  <div className={'h-10 w-10 rounded-full inline-flex items-center justify-center flex-shrink-0 ' + meta.tone}>
                    <EntityIcon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{a.entityName ?? a.entityCode ?? a.entityId.slice(0,8)}</span>
                      <span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ' + meta.tone}>
                        {meta.label}
                      </span>
                      <span className="text-xs text-muted-foreground">{DOC_LABEL[a.docType] ?? a.docType}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                      Expires {a.expiresOn} · {a.daysRemaining < 0 ? Math.abs(a.daysRemaining) + 'd ago' : a.daysRemaining + 'd left'}
                      {a.acknowledgedAt && (
                        <span> · ack by {a.acknowledgedByName ?? 'admin'} {a.acknowledgmentNotes ? ('— ' + a.acknowledgmentNotes) : ''}</span>
                      )}
                    </div>
                  </div>
                  {!a.acknowledgedAt && (
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => ack(a)}>
                        <XIcon className="h-3.5 w-3.5" /> Ack
                      </Button>
                      <Button size="sm" className="gap-1.5" onClick={() => setRenewOpen(a)}>
                        <Plus className="h-3.5 w-3.5" /> Renewed
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {renewOpen && (
        <RenewalSheet alert={renewOpen} onClose={() => setRenewOpen(null)} onSaved={() => { setRenewOpen(null); refresh(); }} />
      )}
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className={'rounded-lg border bg-card p-3 ' + tone}>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-medium mt-1">{value}</div>
    </div>
  );
}

function RenewalSheet({ alert, onClose, onSaved }: { alert: AlertRow; onClose: () => void; onSaved: () => void }) {
  const [newDate, setNewDate] = useState('');
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate)) { setError('Pick a valid new expiry date'); return; }
    setBusy(true);
    try {
      await apiClient.post('/api/v1/document-expiry/renewals', {
        docType: alert.docType,
        entityType: alert.entityType,
        entityId: alert.entityId,
        newExpiryDate: newDate,
        costSar: cost ? Number(cost) : undefined,
        notes: notes.trim() || undefined,
        alertId: alert.id,
      });
      onSaved();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Record renewal
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="rounded-md bg-muted p-3 text-sm">
            <div className="font-medium">{alert.entityName ?? alert.entityCode}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{DOC_LABEL[alert.docType] ?? alert.docType} · current expiry {alert.expiresOn}</div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="newdate">New expiry date</Label>
            <Input id="newdate" type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cost">Cost (SAR) <span className="text-[10px] normal-case tracking-normal font-normal text-muted-foreground">(optional)</span></Label>
            <Input id="cost" type="number" step="0.01" min="0" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="e.g. 350" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes <span className="text-[10px] normal-case tracking-normal font-normal text-muted-foreground">(optional)</span></Label>
            <textarea id="notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base" />
          </div>

          {error && <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Save
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
